var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

// Copyright (c) 2017 Uber Technologies, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import MathArray from './math-array';
import { checkNumber } from './common';

// gl-matrix is too big. Cherry-pick individual imports from stack.gl version
/* eslint-disable camelcase */
import quat_fromMat3 from 'gl-quat/fromMat3';
import quat_identity from 'gl-quat/identity';
import quat_length from 'gl-quat/length';
import quat_squaredLength from 'gl-quat/squaredLength';
import quat_dot from 'gl-quat/dot';
// import quat_getAxisAngle from 'gl-quat/getAxisAngle';
import quat_rotationTo from 'gl-quat/rotationTo';
import quat_add from 'gl-quat/add';
import quat_calculateW from 'gl-quat/calculateW';
import quat_conjugate from 'gl-quat/conjugate';
import quat_invert from 'gl-quat/invert';
import quat_lerp from 'gl-quat/lerp';
import quat_multiply from 'gl-quat/multiply';
import quat_normalize from 'gl-quat/normalize';
import quat_rotateX from 'gl-quat/rotateX';
import quat_rotateY from 'gl-quat/rotateY';
import quat_rotateZ from 'gl-quat/rotateZ';
import quat_scale from 'gl-quat/scale';
import quat_set from 'gl-quat/set';
import quat_setAxisAngle from 'gl-quat/setAxisAngle';
import quat_slerp from 'gl-quat/slerp';

export function validateQuaternion(q) {
  return q.length === 4 && Number.isFinite(q[0]) && Number.isFinite(q[1]) && Number.isFinite(q[2]) && Number.isFinite(q[3]);
}

var Quaternion = function (_MathArray) {
  _inherits(Quaternion, _MathArray);

  // Creates a new identity quat
  function Quaternion() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var z = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var w = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;

    _classCallCheck(this, Quaternion);

    var _this = _possibleConstructorReturn(this, (Quaternion.__proto__ || Object.getPrototypeOf(Quaternion)).call(this));

    if (Array.isArray(x) && arguments.length === 1) {
      _this.copy(x);
    } else {
      _this.set(x, y, z, w);
    }
    return _this;
  }

  // Creates a quaternion from the given 3x3 rotation matrix.
  // NOTE: The resultant quaternion is not normalized, so you should
  // be sure to renormalize the quaternion yourself where necessary.


  _createClass(Quaternion, [{
    key: 'fromMatrix3',
    value: function fromMatrix3(m) {
      quat_fromMat3(this, m);
      this.check();
      return this;
    }

    // Creates a new quat initialized with the given values

  }, {
    key: 'fromValues',
    value: function fromValues(x, y, z, w) {
      return this.set(x, y, z, w);
    }

    // Set a quat to the identity quaternion

  }, {
    key: 'identity',
    value: function identity() {
      quat_identity(this);
      this.check();
      return this;
    }

    // Getters/setters
    /* eslint-disable no-multi-spaces, brace-style, no-return-assign */

  }, {
    key: 'length',

    /* eslint-enable no-multi-spaces, brace-style, no-return-assign */

    // Calculates the length of a quat
    value: function length() {
      return quat_length(this);
    }

    // Calculates the squared length of a quat

  }, {
    key: 'squaredLength',
    value: function squaredLength(a) {
      return quat_squaredLength(this);
    }

    // Calculates the dot product of two quat's
    // @return {Number}

  }, {
    key: 'dot',
    value: function dot(a, b) {
      if (b !== undefined) {
        throw new Error('Quaternion.dot only takes one argument');
      }
      return quat_dot(this, a);
    }

    // Gets the rotation axis and angle for a given quaternion.
    // If a quaternion is created with setAxisAngle, this method will
    // return the same values as providied in the original parameter
    // list OR functionally equivalent values.
    // Example: The quaternion formed by axis [0, 0, 1] and angle -90
    // is the same as the quaternion formed by [0, 0, 1] and 270.
    // This method favors the latter.
    // @return {{[x,y,z], Number}}
    // getAxisAngle() {
    //   const axis = [];
    //   const angle = quat_getAxisAngle(axis, this);
    //   return {axis, angle};
    // }

    // MODIFIERS

    // Sets a quaternion to represent the shortest rotation from one vector
    // to another. Both vectors are assumed to be unit length.

  }, {
    key: 'rotationTo',
    value: function rotationTo(vectorA, vectorB) {
      quat_rotationTo(this, vectorA, vectorB);
      this.check();
      return this;
    }

    // Sets the specified quaternion with values corresponding to the given axes.
    // Each axis is a vec3 and is expected to be unit length and perpendicular
    // to all other specified axes.
    // setAxes() {
    //   Number
    // }

    // Performs a spherical linear interpolation with two control points
    // sqlerp() {
    //   Number;
    // }

    // Adds two quat's

  }, {
    key: 'add',
    value: function add(a, b) {
      if (b !== undefined) {
        throw new Error('Quaternion.add only takes one argument');
      }
      quat_add(this, a);
      this.check();
      return this;
    }

    // Calculates the W component of a quat from the X, Y, and Z components.
    // Any existing W component will be ignored.

  }, {
    key: 'calculateW',
    value: function calculateW() {
      quat_calculateW(this, this);
      this.check();
      return this;
    }

    // Calculates the conjugate of a quat If the quaternion is normalized,
    // this function is faster than quat_inverse and produces the same result.

  }, {
    key: 'conjugate',
    value: function conjugate() {
      quat_conjugate(this, this);
      this.check();
      return this;
    }

    // Calculates the inverse of a quat

  }, {
    key: 'invert',
    value: function invert() {
      quat_invert(this, this);
      this.check();
      return this;
    }

    // Performs a linear interpolation between two quat's

  }, {
    key: 'lerp',
    value: function lerp(a, b, t) {
      quat_lerp(this, a, b, t);
      this.check();
      return this;
    }

    // Multiplies two quat's

  }, {
    key: 'multiply',
    value: function multiply(a, b) {
      if (b !== undefined) {
        throw new Error('Quaternion.multiply only takes one argument');
      }
      quat_multiply(this, this, b);
      this.check();
      return this;
    }

    // Normalize a quat

  }, {
    key: 'normalize',
    value: function normalize() {
      quat_normalize(this, this);
      this.check();
      return this;
    }

    // Rotates a quaternion by the given angle about the X axis

  }, {
    key: 'rotateX',
    value: function rotateX(rad) {
      quat_rotateX(this, this, rad);
      this.check();
      return this;
    }

    // Rotates a quaternion by the given angle about the Y axis

  }, {
    key: 'rotateY',
    value: function rotateY(rad) {
      quat_rotateY(this, this, rad);
      this.check();
      return this;
    }

    // Rotates a quaternion by the given angle about the Z axis

  }, {
    key: 'rotateZ',
    value: function rotateZ(rad) {
      quat_rotateZ(this, this, rad);
      this.check();
      return this;
    }

    // Scales a quat by a scalar number

  }, {
    key: 'scale',
    value: function scale(b) {
      quat_scale(this, this, b);
      this.check();
      return this;
    }

    // Set the components of a quat to the given values

  }, {
    key: 'set',
    value: function set(i, j, k, l) {
      quat_set(this, i, j, k, l);
      this.check();
      return this;
    }

    // Sets a quat from the given angle and rotation axis, then returns it.

  }, {
    key: 'setAxisAngle',
    value: function setAxisAngle(axis, rad) {
      quat_setAxisAngle(this, axis, rad);
      this.check();
      return this;
    }

    // Performs a spherical linear interpolation between two quat

  }, {
    key: 'slerp',
    value: function slerp(a, b, t) {
      quat_slerp(this, a, b, t);
      this.check();
      return this;
    }
  }, {
    key: 'ELEMENTS',
    get: function get() {
      return 4;
    }
  }, {
    key: 'x',
    get: function get() {
      return this[0];
    },
    set: function set(value) {
      return this[0] = checkNumber(value);
    }
  }, {
    key: 'y',
    get: function get() {
      return this[1];
    },
    set: function set(value) {
      return this[1] = checkNumber(value);
    }
  }, {
    key: 'z',
    get: function get() {
      return this[2];
    },
    set: function set(value) {
      return this[2] = checkNumber(value);
    }
  }, {
    key: 'w',
    get: function get() {
      return this[3];
    },
    set: function set(value) {
      return this[3] = checkNumber(value);
    }
  }]);

  return Quaternion;
}(MathArray);

export default Quaternion;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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


//////////////////
// WEBPACK FOOTER
// ./node_modules/math.gl/dist-es6/quaternion.js
// module id = 188
// module chunks = 0